export type LocationStatus = 'pending' | 'granted' | 'denied';

export interface LocationState {
  status: LocationStatus;
  coords: { lat: number; lng: number } | null;
}

export const FALLBACK_COORDS = { lat: 44.6488, lng: -63.5752 };

let _state: LocationState = { status: 'pending', coords: null };
let _initialized = false;
const _listeners = new Set<(s: LocationState) => void>();

function _notify(): void {
  _listeners.forEach((fn) => fn({ ..._state }));
}

export function getLocationState(): LocationState {
  return { ..._state };
}

export function subscribeLocation(fn: (s: LocationState) => void): () => void {
  _listeners.add(fn);
  return () => { _listeners.delete(fn); };
}

export function initLocation(): void {
  if (_initialized || typeof window === 'undefined') return;
  _initialized = true;
  if (!navigator.geolocation) {
    _state = { status: 'denied', coords: null };
    _notify();
    return;
  }
  navigator.geolocation.getCurrentPosition(
    (pos) => {
      _state = { status: 'granted', coords: { lat: pos.coords.latitude, lng: pos.coords.longitude } };
      _notify();
    },
    () => {
      _state = { status: 'denied', coords: null };
      _notify();
    },
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
  );
}

export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) *
    Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
